import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  closeAllPanels,
  closeOrderSidePanel,
  closeProductSidePanel,
  closeItemSidePanel,
  closeSiteSidePanel,
  closeCustomerDetailsModal,
  closeItemPanel,
  closeItemStages,
  backToSites,
  backToOrders,
  backToProducts,
  backToCustomers,
  backToSitesFromCustomer,
  backToProductsFromOrder,
  openOrderSidePanel,
  openProductSidePanel,
  openItemSidePanel,
  openSiteSidePanel,
  openCustomerDetailsModal,
  openItemPanel,
  openItemSidePanelFromOrder as openItemSidePanelFromOrderAction,
  openItemStages,
  setOrdersLoading,
  setProductsLoading,
  setSitesLoading,
} from '../store/slices/sidePanelSlice';

export const useSidePanel = () => {
  const dispatch = useAppDispatch();
  const sidePanelState = useAppSelector((state) => state.sidePanel);

  // Close all panels function
  const closeAll = useCallback(() => {
    dispatch(closeAllPanels());
  }, [dispatch]);

  // Individual close functions (for back button functionality)
  const closeOrderPanel = useCallback(() => {
    dispatch(closeOrderSidePanel());
  }, [dispatch]);

  const closeProductPanel = useCallback(() => {
    dispatch(closeProductSidePanel());
  }, [dispatch]);

  const closeItemPanel = useCallback(() => {
    dispatch(closeItemSidePanel());
  }, [dispatch]);

  const closeSitePanel = useCallback(() => {
    dispatch(closeSiteSidePanel());
  }, [dispatch]);

  const closeCustomerDetails = useCallback(() => {
    dispatch(closeCustomerDetailsModal());
  }, [dispatch]);

  const closeItemPanelFromOrder = useCallback(() => {
    dispatch(closeItemPanel());
  }, [dispatch]);

  const closeItemStagesPanel = useCallback(() => {
    dispatch(closeItemStages());
  }, [dispatch]);

  // Back navigation functions
  const backToSitesFromOrders = useCallback(() => {
    dispatch(backToSites());
  }, [dispatch]);

  const backToOrdersFromProducts = useCallback(() => {
    dispatch(backToOrders());
  }, [dispatch]);

  const backToProductsFromItems = useCallback(() => {
    dispatch(backToProducts());
  }, [dispatch]);

  const backToCustomersFromSites = useCallback(() => {
    dispatch(backToCustomers());
  }, [dispatch]);

  const backToSitesFromCustomerOrders = useCallback(() => {
    dispatch(backToSitesFromCustomer());
  }, [dispatch]);

  const backToProductsFromOrderItems = useCallback(() => {
    dispatch(backToProductsFromOrder());
  }, [dispatch]);

  // Open functions
  const openOrderPanel = useCallback((site: any, orders: any[]) => {
    dispatch(openOrderSidePanel({ site, orders }));
  }, [dispatch]);

  const openProductPanel = useCallback((order: any, products: any[]) => {
    dispatch(openProductSidePanel({ order, products }));
  }, [dispatch]);

  const openItemPanelFromProducts = useCallback((product: any, order: any) => {
    dispatch(openItemSidePanel({ product, order }));
  }, [dispatch]);

  const openSitePanel = useCallback((customer: any, sites: any[]) => {
    dispatch(openSiteSidePanel({ customer, sites }));
  }, [dispatch]);

  const openCustomerDetails = useCallback((customer: any, site: any, order: any) => {
    dispatch(openCustomerDetailsModal({ customer, site, order }));
  }, [dispatch]);

  const openItemPanelFromOrder = useCallback((product: any) => {
    dispatch(openItemPanel({ product }));
  }, [dispatch]);

  const openItemSidePanelFromOrder = useCallback((product: any) => {
    dispatch(openItemSidePanelFromOrderAction({ product }));
  }, [dispatch]);

  const openItemStagesPanel = useCallback((item: any) => {
    dispatch(openItemStages({ item }));
  }, [dispatch]);

  // Loading state functions
  const setOrdersLoadingState = useCallback((loading: boolean) => {
    dispatch(setOrdersLoading(loading));
  }, [dispatch]);

  const setProductsLoadingState = useCallback((loading: boolean) => {
    dispatch(setProductsLoading(loading));
  }, [dispatch]);

  const setSitesLoadingState = useCallback((loading: boolean) => {
    dispatch(setSitesLoading(loading));
  }, [dispatch]);

  return {
    // State
    ...sidePanelState,
    
    // Close functions
    closeAll,
    closeOrderPanel,
    closeProductPanel,
    closeItemPanel,
    closeSitePanel,
    closeCustomerDetails,
    closeItemPanelFromOrder,
    closeItemStagesPanel,
    
    // Back navigation functions
    backToSitesFromOrders,
    backToOrdersFromProducts,
    backToProductsFromItems,
    backToCustomersFromSites,
    backToSitesFromCustomerOrders,
    backToProductsFromOrderItems,
    
    // Open functions
    openOrderPanel,
    openProductPanel,
    openItemPanelFromProducts,
    openSitePanel,
    openCustomerDetails,
    openItemPanelFromOrder,
    openItemSidePanelFromOrder,
    openItemStagesPanel,
    
    // Loading functions
    setOrdersLoadingState,
    setProductsLoadingState,
    setSitesLoadingState,
  };
};
